import { Ship, Package, Calendar, Truck, Weight, Boxes, FileCheck2 } from "lucide-react";
import { totalContainerWeight } from "../../utils/oceanContainerWeight";

// Confirmed against a real admin/booking-history?cargoType=Ocean+Cargo record:
// originPort/destinationPort are always objects — { originPort: "Mundra - (INMUN1)", ... }
// / { destinationPort: "Hamburg - (DEHAM)", ... } — already-formatted "City - (CODE)"
// strings, so no flag/country-code lookup is available or needed here.
const portLabel = (portObj, key) => (portObj && typeof portObj === "object" ? portObj[key] : "") || "";

// Route banner for the Ocean manual-quote page — mirrors ManualQuoteHeader.jsx
// (air) visually, but reads the ocean query shape returned by
// admin/booking-history?cargoType=Ocean+Cargo: top-level `mode`,
// a flat `containerDetails: [{ type, quantity, kgs }]` array, and
// `paymentTerms.incoTerms` (see Queries.jsx's ocean table rendering).
const OceanManualQuoteHeader = ({ queryDetails }) => {
  const originPort = portLabel(queryDetails?.originPort, "originPort");
  const destinationPort = portLabel(queryDetails?.destinationPort, "destinationPort");

  const cargoDate = queryDetails?.goodsDetails?.cargoreadydate;
  const commodityType =
    queryDetails?.goodsDetails?.commonditytype?.commonditytype ||
    queryDetails?.goodsDetails?.commodity_description;
  const incoTerms = queryDetails?.paymentTerms?.incoTerms;

  const mode = (queryDetails?.mode || "").toUpperCase();
  const isFCL = mode !== "LCL";
  const containerRows = Array.isArray(queryDetails?.containerDetails)
    ? queryDetails.containerDetails
    : [];
  const containerCount = containerRows.reduce(
    (sum, c) => sum + (Number(c?.quantity) || 0),
    0
  );
  // kgs is the line total (legacy rows without weightType are per container).
  const totalKgs = totalContainerWeight(containerRows);

  const needsTransport =
    queryDetails?.originPort?.originPickup || queryDetails?.destinationPort?.destinationPickup;

  return (
    <div
      className="mt-6 rounded-2xl overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #fffbf0 0%, #fff8e1 100%)",
        border: "1px solid #f0dea3",
        boxShadow: "0 4px 16px rgba(184, 110, 0, .08)",
      }}
    >
      <div className="px-6 py-5 flex flex-wrap items-center justify-between gap-4">
        {/* Route */}
        <div className="flex items-center gap-5">
          <p className="text-xl sm:text-2xl font-bold text-gray-800 leading-none max-w-[220px] truncate" title={originPort}>
            {originPort || "—"}
          </p>

          <div className="flex items-center gap-1.5">
            <div style={{ width: 36, borderTop: "2px dashed #E7B24D" }} />
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center flex-none"
              style={{ background: "#F5A623", boxShadow: "0 4px 10px rgba(245,166,35,.4)" }}
            >
              <Ship className="w-4 h-4 text-white" />
            </div>
            <div style={{ width: 36, borderTop: "2px dashed #E7B24D" }} />
          </div>

          <p className="text-xl sm:text-2xl font-bold text-gray-800 leading-none max-w-[220px] truncate" title={destinationPort}>
            {destinationPort || "—"}
          </p>
        </div>

        {/* Info pills */}
        <div className="flex flex-wrap items-center gap-2">
          {cargoDate && (
            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm">
              <Calendar className="w-3.5 h-3.5 text-primary" />
              {cargoDate}
            </div>
          )}
          <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm">
            <Boxes className="w-3.5 h-3.5 text-primary" />
            {mode || (isFCL ? "FCL" : "LCL")}
          </div>
          {commodityType && (
            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm">
              <Package className="w-3.5 h-3.5 text-primary" />
              {commodityType}
            </div>
          )}
          {incoTerms && (
            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm">
              <FileCheck2 className="w-3.5 h-3.5 text-primary" />
              {incoTerms}
            </div>
          )}
          {isFCL && containerCount > 0 && (
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm">
              {containerCount} {containerCount === 1 ? "Container" : "Containers"}
            </div>
          )}
          {totalKgs > 0 && (
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm">
              <Weight className="w-3 h-3 text-gray-400" />
              G.W {totalKgs} KG
            </div>
          )}
          <div
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm ${
              needsTransport
                ? "bg-blue-50 border border-blue-200 text-blue-700"
                : "bg-gray-50 border border-gray-200 text-gray-500"
            }`}
          >
            <Truck className="w-3.5 h-3.5" />
            {needsTransport ? "Transport Required" : "No Transport"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OceanManualQuoteHeader;
